/**
 * A playing card, in the Seaside pack.
 *
 * Suit is carried by **shape and hue together**, with four distinct colours rather
 * than red-and-black, so the cards work for colour-blind players. scope.md §9.
 */

import type { Card as CardModel, Rank, Suit } from '@garbage/shared';
import { isJoker } from '@garbage/shared';
import { DEFAULT_PACK, PACKS, type PackId } from '../packs.js';
import { useT } from '../i18n.js';

const SUIT_GLYPH: Record<Suit, string> = {
  spades: '♠', hearts: '♥', diamonds: '♦', clubs: '♣',
};

export type { PackId };

export const SIZES = {
  tiny: 'w-7 h-10 rounded',
  small: 'w-9 h-13 rounded-md',
  medium: 'w-12 h-17 rounded-lg',
  large: 'w-16 h-24 rounded-xl',
} as const;

export type CardSize = keyof typeof SIZES;

const RANK_SIZE: Record<CardSize, string> = {
  tiny: '11px', small: '13px', medium: '17px', large: '23px',
};
const PIP_SIZE: Record<CardSize, string> = {
  tiny: '9px', small: '11px', medium: '13px', large: '18px',
};

export function rankLabel(rank: Rank): string {
  return rank === 'JOKER' ? '★' : rank;
}

/** A card as a short piece of text, such as "7♦" — for announcements, not faces. */
export function cardName(card: CardModel, jokerWord = 'joker'): string {
  return isJoker(card) ? jokerWord : `${rankLabel(card.rank)}${card.suit ? SUIT_GLYPH[card.suit] : ''}`;
}

export function CardFace({
  card, size = 'medium', dimmed = false, selected = false, marked = false, corner = false,
  pack = DEFAULT_PACK,
}: {
  card: CardModel;
  size?: CardSize;
  dimmed?: boolean;
  selected?: boolean;
  /** A joker inside a meld, standing in for something. */
  marked?: boolean;
  /**
   * Rank and suit in the top-left corner rather than the middle, so the card can be
   * overlapped by the next one and still be read — as in a fanned meld on the table.
   */
  corner?: boolean;
  pack?: PackId;
}) {
  const t = useT();
  const design = PACKS[pack] ?? PACKS[DEFAULT_PACK];
  const wild = isJoker(card);
  const colour = card.suit ? design.suits[card.suit] : design.joker.colour;

  return (
    <div
      data-testid={`card-${card.id}`}
      data-rank={card.rank}
      className={[
        'relative flex flex-col shrink-0',
        corner ? 'items-start justify-start px-0.5 pt-0.5' : 'items-center justify-center',
        'border shadow-md select-none transition-transform',
        SIZES[size],
        dimmed ? 'opacity-40 saturate-50' : '',
        selected ? 'ring-4 ring-sun -translate-y-2' : '',
        marked ? 'ring-2 ring-violet-500' : '',
      ].join(' ')}
      style={{
        // A joker is the most valuable card in the game and must never be mistaken
        // for a number. It gets its own tint, its own colour, and says what it is.
        background: wild ? design.joker.tint : design.face,
        borderColor: wild ? design.joker.edge : design.faceEdge,
        color: colour,
      }}
    >
      {wild ? (
        <>
          <span className="font-black leading-none" style={{ fontSize: RANK_SIZE[size] }}>★</span>
          <span
            className="font-bold leading-none tracking-wider"
            style={{ fontSize: size === 'tiny' ? '6px' : size === 'small' ? '7px' : '8px' }}
          >
            {t('card.wild')}
          </span>
        </>
      ) : (
        <>
          <span className="font-black leading-none" style={{ fontSize: RANK_SIZE[size] }}>
            {rankLabel(card.rank)}
          </span>
          <span className="leading-none" style={{ fontSize: PIP_SIZE[size] }} aria-hidden="true">
            {card.suit ? SUIT_GLYPH[card.suit] : ''}
          </span>
        </>
      )}
    </div>
  );
}

/** A face-down card. Never carries any information about what it is. */
export function CardBack({
  size = 'medium', pack = DEFAULT_PACK,
}: { size?: CardSize; pack?: PackId }) {
  const design = (PACKS[pack] ?? PACKS[DEFAULT_PACK]).back;
  return (
    <div
      data-testid="card-back"
      className={`border shadow-md shrink-0 flex items-center justify-center ${SIZES[size]}`}
      style={{ background: design.background, borderColor: design.edge, color: design.markColour }}
    >
      <span aria-hidden="true" style={{ fontSize: PIP_SIZE[size] }}>{design.mark}</span>
    </div>
  );
}

export function EmptyPile({ size = 'medium' }: { size?: CardSize }) {
  return <div className={`border-2 border-dashed border-bone/20 shrink-0 ${SIZES[size]}`} />;
}
